<template>
  <div class="payroll-request-form" ref="form">
    <!-- Pinwheel information box -->
    <div
      v-if="organizationDetails.payrollDonationMethod == 'PINWHEEL_API' && !hasExternalDonation"
      class="form-field info-container"
    >
      <div class="tooltip-icon">
        <img src="@/assets/icons/information-icon.png" />
      </div>
      <div class="tooltip">
        <div class="tooltip-header" v-translate>
          Set up a recurring donation directly from your paycheck
        </div>
        <div class="tooltip-text">
          <p>Use your HR / payroll provider login to contribute to the {{organizationDetails.programName}}.</p>
          <p v-translate>
            To get started, enter the amount you'd like to donate below, then click the "Make A
            Donation" button.
          </p>
        </div>
      </div>
    </div>
    <div v-if="isPinwheelAPI && hasExternalDonation" class="form-field info-container">
      <div class="tooltip-icon">
        <img src="@/assets/icons/information-icon.png" />
      </div>
      <div class="tooltip">
        <div class="tooltip-header" v-translate>Thank you for your contribution!</div>
        <div class="tooltip-text">
          <p>
            <translate
              >It looks like you've already set up a recurring donation via your payroll account.
              You may edit the amount below, or</translate
            >&nbsp;
            <a @click="removeExternalDonation"><translate>cancel this donation</translate></a
            >.
          </p>
        </div>
      </div>
    </div>

    <div :class="{ 'pinwheel-form': isPinwheelAPI }">
      <!-- Payment frequency -->
      <div v-if="!isPinwheelAPI" class="form-field">
        <h4 class="form-field-header" v-translate>Select payment frequency</h4>
        <div class="form-field-container">
          <CheckboxRadioButtons
            name="payroll-frequency-options"
            :options="PayrollRequest.options.FREQUENCY_CHOICES"
            :initial="defaultFrequencyOption"
            @selected="onChangeFrequency"
          />
        </div>
      </div>

      <!-- Amount -->
      <div v-if="" class="form-field amount-field">
        <h4 class="form-field-header" v-translate>Amount</h4>
        <div class="form-field-container">
          <md-field class="amount-input form-field-text">
            <md-icon class="md-18" style="font-size: 18px !important">attach_money</md-icon>
            <md-input v-model="payrollRequest.amount" type="number" required></md-input>
          </md-field>
        </div>
      </div>

      <template v-if="!isPinwheelAPI">
        <!-- Employee Details -->
        <div class="form-field">
          <h3 class="employee-details" v-translate>Employee details</h3>
        </div>

        <div class="row-wrapper">
          <!-- First name -->
          <FormTextField :label="$gettext('First name')" :value.sync="payrollRequest.firstName" autoComplete="given-name" />

          <!-- Last name -->
          <FormTextField :label="$gettext('Last name')" :value.sync="payrollRequest.lastName" autoComplete="family-name" />
        </div>

        <div class="row-wrapper clear-margin">
          <!-- Work email -->
          <FormTextField
            :label="$gettext('Work email address')"
            :value.sync="payrollRequest.email"
            :showErrorMessage="hasSubmitted && emailInvalid"
            errorMessage="Please enter a valid email address."
            helpText="example@email.com"
          />

          <!-- Unique ID -->
          <FormTextField
            :label="$gettext('Unique ID')"
            :value.sync="payrollRequest.employeeId"
            :helpText="organizationDetails.uniqueIdHelpCopy"
          />
        </div>

        <!-- Zip code -->
        <FormTextField
          v-if="!userIsLoggedIn"
          :label="$gettext('Zip Code')"
          :value.sync="payrollRequest.zipCode"
          :showErrorMessage="hasSubmitted && !zipCodeValid"
          errorMessage="Please enter a valid, 5-digit zip code. If your zip code is less than five digits add leading zeros (e.g. 00123)"
          helpText="#####"
          class="zip-field"
          autoComplete="postal-code"
        />
      </template>

      <!-- Submit -->
      <div class="button-container">
        <LoadingSpinnerButton
          :loading="loading"
          :text="this.next()"
          class="form-button"
          @click="handleSubmit"
        />
        <LoadingSpinnerButton
          v-if="isPinwheelAPI && hasExternalDonation"
          text="Cancel Donation"
          class="form-button white-button"
          @click="removeExternalDonation"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import CheckboxRadioButtons from '@/components/CheckboxRadioButtons'
import PayrollRequest from '@/services/payrollRequests'
import HelpIcon from '@/components/HelpIcon'
import LoadingSpinnerButton from '@/components/LoadingSpinnerButton'
import User from '@/services/users'
import FormTextField from '@/components/forms/FormTextField'
import PinwheelApi from '@/services/pinwheel'

export default {
  name: 'PayrollRequestForm',
  components: {
    CheckboxRadioButtons,
    HelpIcon,
    LoadingSpinnerButton,
    FormTextField,
  },
  data() {
    return {
      PayrollRequest,
      payrollRequest: PayrollRequest.create({
        frequency: PayrollRequest.options.RECURRING,
      }),
      showHelp: false,
      loading: false,
      hasSubmitted: false,
      link_token: {},
      payrollSwitchAdded: false,
      external_donations: [],
    }
  },
  async created() {
    // First, pull in any relevant data from logged-in user
    if (this.userIsLoggedIn) {
      this.payrollRequest.firstName = this.user.firstName
      this.payrollRequest.lastName = this.user.lastName
      this.payrollRequest.email = this.user.email
      this.payrollRequest.zipCode = this.user.zipCode
      this.payrollRequest.employeeId = this.user.employeeCodeRef
        ? this.user.employeeCodeRef.code
        : ''
    }

    // If there's already relevant data in state, repopulate from there
    if (this.currentPayrollRequest) {
      this.payrollRequest = this.currentPayrollRequest
    }

    if (this.organizationDetails.payrollDonationMethod == 'PINWHEEL_API') {
      this.external_donations = JSON.parse(await PinwheelApi.getUserExternalDonations())
      if (this.hasExternalDonation)
        this.payrollRequest.amount = parseFloat(this.external_donations[0].amount)
    }
  },
  methods: {
    next() {
      if (this.isPinwheelAPI && this.hasExternalDonation) {
        return this.$gettext('Update Amount')
      } else if (this.isPinwheelAPI && !this.hasExternalDonation) {
        return this.$gettext('Make A Donation')
      } else {
        return this.$gettext('Next')
      }
    },
    onChangeFrequency(option) {
      this.payrollRequest.frequency = option.value
    },
    clickedHelp() {
      // if showing, close and remove event listener
      // if not showing, open and add event listener
      if (!this.showHelp) {
        this.showHelp = true
        setTimeout(() => this.$refs.form.addEventListener('click', this.helpTextCallBack), 0)
      }
    },
    helpTextCallBack() {
      this.showHelp = false
      this.$refs.form.removeEventListener('click', this.helpTextCallBack)
    },
    handleSubmit() {
      this.hasSubmitted = true
      this.loading = true

      if (!this.isPinwheelAPI) {
        this.onNext()
      } else {
        this.handlePinwheelModal()
      }
    },
    async onNext() {
      // Format payroll request appropriately before saving to state
      const amount = Number(this.payrollRequest.amount).toFixed(2)
      this.payrollRequest.amount = amount
      this.$store.dispatch('updateCurrentPayrollRequest', this.payrollRequest)

      this.$router.push({ name: 'PayrollReview' })
    },
    async handlePinwheelModal() {
      // Get a Pinwheel link token, then open the modal
      let link_token = await PinwheelApi.getPayrollContributionLinkToken(
        this.$language.current,
        Number(this.payrollRequest.amount),
      )
      this.link_token = link_token

      if (link_token) {
        Pinwheel.open({ linkToken: link_token.token, onEvent: this.handlePinwheelLinkEvent })
      }
    },
    async handlePinwheelLinkEvent(name, payload) {
      switch (name) {
        case 'open':
          // Link modal now open, hide the spinner
          this.loading = false
          break

        case 'exit':
          if (this.payrollSwitchAdded) {
            this.external_donations = JSON.parse(await PinwheelApi.getUserExternalDonations())
            if (this.hasExternalDonation) {
              this.payrollRequest.amount = parseFloat(this.external_donations[0].amount)
            } else {
              this.payrollRequest.amount = ''
            }
          }
          break

        case 'success':
          this.payrollSwitchAdded = true
          break

        default:
          break
      }
    },
    removeExternalDonation() {
      this.payrollRequest.amount = 0
      this.handlePinwheelModal()
    },
  },
  mounted() {
    // Load external Pinwheel API script
    let pinwheel_api_script = document.createElement('script')
    pinwheel_api_script.setAttribute('src', 'https://cdn.getpinwheel.com/pinwheel-v2.3.js')
    document.head.appendChild(pinwheel_api_script)
  },
  computed: {
    ...mapState(['user', 'organizationDetails', 'currentPayrollRequest']),
    ...mapGetters(['userIsLoggedIn']),
    /* Select frequency option to be selected by default. */
    defaultFrequencyOption() {
      return PayrollRequest.options.FREQUENCY_CHOICES.find(
        (choice) => choice.value === this.payrollRequest.frequency,
      )
    },
    /*
     * Disable advancing to next page if info is missing.
     */
    disabled() {
      return (
        !this.payrollRequest.firstName ||
        !this.payrollRequest.lastName ||
        !this.payrollRequest.email ||
        !this.payrollRequest.employeeId ||
        (!this.payrollRequest.zipCode && !this.userIsLoggedIn) ||
        !this.payrollRequest.amount ||
        !this.payrollRequest.frequency ||
        !this.zipCodeValid ||
        this.emailInvalid
      )
    },
    zipCodeInvalid() {
      if (this.userIsLoggedIn) {
        return false
      } else {
        return (
          this.payrollRequest.zipCode &&
          (this.payrollRequest.zipCode.length > 10 || this.payrollRequest.zipCode.length < 5)
        )
      }
    },
    zipCodeValid() {
      if (this.userIsLoggedIn) {
        return true
      }

      if (this.payrollRequest.zipCode.length === 0) {
        return true
      }

      const zipRegex = new RegExp(/^[0-9]{5}?$/)

      return (
        this.payrollRequest.zipCode &&
        this.payrollRequest.zipCode.length > 1 &&
        this.payrollRequest.zipCode.length === 5 &&
        !(this.errors && this.errors.zipCode) &&
        zipRegex.test(this.payrollRequest.zipCode)
      )
    },
    emailInvalid() {
      return this.payrollRequest.email && !User.validateEmail(this.payrollRequest.email)
    },
    hasExternalDonation() {
      return this.external_donations.length > 0
    },
    isPinwheelAPI() {
      return this.organizationDetails.payrollDonationMethod === 'PINWHEEL_API'
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';
@import '@/styles/mixins/responsive';

.payroll-request-form {
  display: flex;
  flex-direction: column;
}

.md-field .md-input {
  border-bottom: 0;
}

.row-wrapper {
  margin-top: 0;

  &.clear-margin {
    .form-field {
      margin-bottom: 0;
    }
  }
}

.employee-details {
  color: black;
  font-size: 1.5rem;
  font-weight: bold;
  line-height: 2rem;
  margin-top: 2.5rem;
}

.pinwheel-form {
  display: flex;
  flex-direction: column;

  .amount-field {
    margin-bottom: 0;
    width: 100%;
    max-width: 396px;
  }

  .button-container {
    margin-top: 2rem;
    width: 100%;
  }
}

@include tablet-and-up-screen {
  .pinwheel-form {
    width: 100%;
  }

  .amount-field,
  .zip-field {
    max-width: 396px;
  }
}

.amount-input {
  padding: 0.75rem;
}

.button-container {
  margin-top: 3.5rem;
}

.remove-button {
  background-color: red;
}

.tooltip-icon {
  margin-right: 0.5rem;
}

.tooltip-header {
  margin-bottom: 1rem !important;
}

.tooltip-text p:last-of-type {
  margin-bottom: 0;
}
</style>
